/**
 * Ajanda istemci tipleri (sözleşme: `specs/125-ajanda-modulu/contracts/agendas-api.md`).
 * `startTime`/`endTime`/`responseTime` bölgesiz Türkiye duvar saatidir (`yyyy-MM-ddTHH:mm:00`);
 * `modDate` bir andır, yalnız optimistic lock için geri gönderilir. Sunucu boş alanları hiç yazmaz
 * (`NON_NULL`), bu yüzden boş kalabilen alanlar opsiyoneldir.
 */

/** Katılım yanıtı (`IS_PARTICIPATE` boş / 11 / 10 sunucuda bu adlara çevrilir). */
export type AgendaResponse = 'PENDING' | 'ATTENDING' | 'NOT_ATTENDING'

/** Katılımcının verebileceği yanıtlar — "Yanıt Bekleniyor"a katılımcı kendisi dönemez. */
export type AgendaAnswer = Exclude<AgendaResponse, 'PENDING'>

/**
 * Tekrar sıklığı (126; sözleşme `specs/126-ajanda-tekrarli-toplanti/contracts/agendas-series-api.md`).
 * `DAILY` iş günleridir; üçünde de tam gün tatiller atlanır.
 */
export type AgendaRecurrence = 'DAILY' | 'WEEKLY' | 'MONTHLY'

/** Formdaki sıra — görseldeki sırayla değil, sıklık sırasıyla. */
export const RECURRENCE_TYPES: readonly AgendaRecurrence[] = ['DAILY', 'WEEKLY', 'MONTHLY']

/**
 * Güncelleme/silmenin kapsamı (126). Sunucu varsayılanı `OCCURRENCE` olduğu için istemci yalnız `SERIES`'i
 * gönderir — 125'in çağrıları birebir aynı kalır.
 */
export type AgendaScope = 'OCCURRENCE' | 'SERIES'

/** Toplantının bağlı olduğu seri; `endDate` `YYYY-MM-DD` (dahil). */
export interface AgendaSeries {
  id: number
  type: AgendaRecurrence
  endDate: string
}

/** Haftalık takvimin satırı (`GET /agendas/calendar`). Yanıt dağılımı taşımaz. */
export interface AgendaListItem {
  id: number
  title: string
  startTime: string
  endTime: string
  ownerUserId: number
  ownerFullName?: string | null
  /** Görüntüleyen bu toplantının sahibi mi. */
  owner: boolean
  /** Görüntüleyen katılımcıysa kendi yanıtı; değilse alan gelmez. */
  myResponse?: AgendaResponse
  /** Aktif katılımcı sayısı. */
  participantCount: number
  /** Tekrarlı serinin kimliği (126); tek toplantıda gelmez. */
  seriesId?: number
}

/** Katılımcı — İK seçicisinden anlık görüntü; detayda döner, kayıt gövdesinde aynen gider. */
export interface AgendaParticipant {
  /** Sicil. */
  employeeId: string
  /** Grup Daveti ile eklendiyse listenin kimliği (yalnız kayıt gövdesinde; sunucu yeni satıra yazar). */
  managerListId?: number | null
  firstName?: string | null
  lastName?: string | null
  businessMail?: string | null
  jobTitleTr?: string | null
  jobTitleEn?: string | null
  /**
   * Bir davetlinin yerine katılıyor (131, 2026-11-03) — herkese döner, yalnız işarettir; **kimin** yerine
   * olduğu burada yok. Kayıt gövdesine gönderilmez (form alanları tek tek kurar).
   */
  substituting?: boolean | null
}

/** Yerine katılma bağının karşı tarafı (131) — yalnız kimlik ve ad; mazeret taşımaz. */
export interface AgendaPersonRef {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
}

export interface AgendaParticipantResponse {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  response: AgendaResponse
  reason?: string | null
  /** Bu davetlinin yerine katılacak kişi (131). */
  substitute?: AgendaPersonRef | null
  /** Bu kişi kimin yerine katılıyor (131). */
  substituteFor?: AgendaPersonRef | null
}

export interface AgendaResponseSummary {
  attending: number
  notAttending: number
  pending: number
}

export interface MyParticipation {
  participantId: number
  response: AgendaResponse
  reason?: string | null
  responseTime?: string | null
  /**
   * `startTime > now` ve yanıt kilitli değil (131: başkasının yerine katılan kişinin yanıtı sabittir) —
   * sunucu karar verir; istemci saatine bakılmaz.
   */
  respondable: boolean
  /** Katılımcı satırının damgası — yanıt gövdesine geri gönderilir. */
  modDate: string
  /** Görüntüleyenin yerine katılacak kişi (131) — Katılmayacağım formunu doldurur. */
  substitute?: AgendaPersonRef | null
  /** Görüntüleyen kimin yerine katılıyor (131) — varsa yanıt kilitlidir. */
  substituteFor?: AgendaPersonRef | null
}

/**
 * Toplantı detayı; gövde görüntüleyene göre gelir: `responses`/`summary` yalnız sahibe,
 * `myParticipation` yalnız katılımcıya. Ekran alanın varlığına bakarak dal seçer.
 */
export interface AgendaDetail {
  id: number
  title: string
  description?: string | null
  startTime: string
  endTime: string
  ownerUserId: number
  ownerFullName?: string | null
  /** Toplantıyı oluşturanın sicili (131) — yerime katılacak kişi olarak seçilemez, önerilerden düşülür. */
  ownerEmployeeId?: string | null
  owner: boolean
  /** Sahip ve `endTime >= now` — sunucu karar verir. */
  editable: boolean
  modDate: string
  participants: AgendaParticipant[]
  responses?: AgendaParticipantResponse[]
  summary?: AgendaResponseSummary
  myParticipation?: MyParticipation | null
  /** Toplantı bir tekrarlı seriye bağlıysa (126). */
  series?: AgendaSeries
}

/**
 * Gövdedeki tekrar (126): oluşturmada ikisi zorunlu; "Serinin tamamı"nda yalnız `endDate` (yeni bitiş)
 * gönderilir, sıklık değişmez.
 */
export interface AgendaRecurrenceInput {
  type?: AgendaRecurrence | null
  endDate: string
}

/** `POST`/`PUT` gövdesi (`PUT`'ta `modDate` zorunlu). `participants` tam listedir. */
export interface AgendaUpsert {
  title: string
  description: string | null
  startTime: string
  endTime: string
  participants: AgendaParticipant[]
  modDate?: string
  recurrence?: AgendaRecurrenceInput
}

export interface AgendaRespond {
  response: AgendaAnswer
  /** Katılmayacağım'da zorunlu; Katılacağım'da `null`. */
  reason: string | null
  /** Yerine katılacak kişinin sicili (131) — Katılmayacağım'da zorunlu; Katılacağım'da `null`. */
  substituteEmployeeId: string | null
  /** `myParticipation.modDate`. */
  modDate: string
}

/** Yanıt → i18n anahtarı. Tanınmayan değer "Yanıt Bekleniyor"a düşer. */
export function responseKey(response: AgendaResponse | null | undefined): string {
  switch (response) {
    case 'ATTENDING':
      return 'agenda.status.attending'
    case 'NOT_ATTENDING':
      return 'agenda.status.notAttending'
    default:
      return 'agenda.status.pending'
  }
}

/** "Ad Soyad" — eksik parça boşluk bırakmaz; ad hiç yoksa sicil gösterilir. */
export function personName(p: {
  employeeId?: string
  firstName?: string | null
  lastName?: string | null
}): string {
  const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()
  return name || (p.employeeId ?? '')
}

/** Sicil karşılaştırma anahtarı: sıfırlı/sıfırsız yazım aynı kişidir (sunucuyla aynı kural). */
export function sicilKey(employeeId: string): string {
  return employeeId.trim().replace(/^0+/, '')
}
